import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from '@/Api/api';

const STORAGE_KEY = 'entity_cache';
const SUCCESS_CACHE_DURATION = 14 * 24 * 60 * 60 * 1000; // 2 weeks in milliseconds
const FAILURE_CACHE_DURATION = 1 * 24 * 60 * 60 * 1000; // 1 day in milliseconds
const DEBUG_LOGGING = false; // Set to true to enable EntityCache debug logs

export const useEntityCacheStore = defineStore('entityCache', () => {
    // State
    const creatures = ref({});
    const npcs = ref({});
    const characters = ref({});
    const equipment = ref({});
    const queue = ref([]);
    const pendingRequest = ref(false);

    let debounceTimer = null;

    // Helper function to convert entity type to stateMap key
    // Equipment is already plural, others need 's' added
    function getTypeKey(type) {
        const typeMap = {
            'Creature': 'creatures',
            'Npc': 'npcs',
            'Character': 'characters',
            'Equipment': 'equipment',
        };
        return typeMap[type] || null;
    }

    // Initialize from localStorage
    function initialize() {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            try {
                const data = JSON.parse(stored);

                // Load cached entities, filtering out expired ones
                const now = Date.now();
                creatures.value = filterExpiredEntities(data.creatures || {}, now);
                npcs.value = filterExpiredEntities(data.npcs || {}, now);
                characters.value = filterExpiredEntities(data.characters || {}, now);
                equipment.value = filterExpiredEntities(data.equipment || {}, now);

                if (DEBUG_LOGGING) {
                    console.log('[EntityCache] 📂 Loaded from localStorage:', {
                        creatures: Object.keys(creatures.value).length,
                        npcs: Object.keys(npcs.value).length,
                        characters: Object.keys(characters.value).length,
                        equipment: Object.keys(equipment.value).length,
                    });
                }
            } catch (error) {
                console.error('[EntityCache] ❌ Failed to parse localStorage cache:', error);
                clearCache();
            }
        }
    }

    // Filter out expired entities
    function filterExpiredEntities(entities, now) {
        const filtered = {};
        for (const [id, entity] of Object.entries(entities)) {
            if (entity.expires_at && entity.expires_at > now) {
                filtered[id] = entity;
            } else if (DEBUG_LOGGING) {
                console.log(`[EntityCache] 🗑️ Expired entity removed: ${id}`);
            }
        }
        return filtered;
    }

    // Persist to localStorage
    function persist() {
        const data = {
            creatures: creatures.value,
            npcs: npcs.value,
            characters: characters.value,
            equipment: equipment.value,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        if (DEBUG_LOGGING) {
            console.log('[EntityCache] 💾 Persisted to localStorage');
        }
    }

    // Get entity from cache
    function getEntity(entityRef) {
        const { type, id } = parseEntityRef(entityRef);
        if (!type || !id) return null;

        const typeKey = getTypeKey(type);
        const stateMap = { creatures, npcs, characters, equipment };
        const entity = stateMap[typeKey]?.value[id] ?? null;

        if (DEBUG_LOGGING) {
            if (entity) {
                console.log(`[EntityCache] ✅ Cache hit for ${entityRef}`, entity);
            } else {
                console.log(`[EntityCache] ❌ Cache miss for ${entityRef}`);
            }
        }

        return entity;
    }

    // Check if entity is cached and not expired
    function isCached(entityRef) {
        const entity = getEntity(entityRef);
        if (!entity) return false;

        // Check if expired
        if (entity.expires_at && entity.expires_at <= Date.now()) {
            if (DEBUG_LOGGING) {
                console.log(`[EntityCache] ⏰ Entity expired: ${entityRef}`);
            }
            return false;
        }

        return true;
    }

    // Request entity to be cached
    function requestEntity(entityRef) {
        const { type, id } = parseEntityRef(entityRef);
        if (!type || !id) {
            console.warn(`[EntityCache] ⚠️ Invalid entity reference: ${entityRef}`);
            return;
        }

        // Check if already cached
        if (isCached(entityRef)) {
            if (DEBUG_LOGGING) {
                console.log(`[EntityCache] ⏭️ Skipping ${entityRef} - already cached`);
            }
            return;
        }

        // Check if already in queue
        const alreadyQueued = queue.value.some(
            item => item.type === type && item.id === id
        );
        if (alreadyQueued) {
            if (DEBUG_LOGGING) {
                console.log(`[EntityCache] ⏭️ Skipping ${entityRef} - already in queue`);
            }
            return;
        }

        // Add to queue
        queue.value.push({ type, id });
        if (DEBUG_LOGGING) {
            console.log(`[EntityCache] ➕ Queued ${entityRef} (Queue size: ${queue.value.length})`);
        }

        // Trigger debounced fetch
        debouncedFetch();
    }

    // Request multiple entities
    function requestEntities(entityRefs) {
        if (DEBUG_LOGGING) {
            console.log(`[EntityCache] 📦 Requesting ${entityRefs.length} entities:`, entityRefs);
        }
        entityRefs.forEach(ref => requestEntity(ref));
    }

    // Debounced fetch
    function debouncedFetch() {
        if (debounceTimer) {
            if (DEBUG_LOGGING) {
                console.log(`[EntityCache] ⏱️ Resetting debounce timer`);
            }
            clearTimeout(debounceTimer);
        }

        if (DEBUG_LOGGING) {
            console.log(`[EntityCache] ⏱️ Starting 500ms debounce timer`);
        }

        debounceTimer = setTimeout(() => {
            if (DEBUG_LOGGING) {
                console.log(`[EntityCache] ⏰ Debounce timer expired - fetching now`);
            }
            fetchQueuedEntities();
        }, 500);
    }

    // Fetch queued entities from API
    async function fetchQueuedEntities() {
        if (queue.value.length === 0) {
            if (DEBUG_LOGGING) {
                console.log(`[EntityCache] ⏭️ Queue is empty - nothing to fetch`);
            }
            return;
        }

        if (pendingRequest.value) {
            if (DEBUG_LOGGING) {
                console.log(`[EntityCache] ⏭️ Request already pending - skipping`);
            }
            return;
        }

        // Copy and clear queue
        const entitiesToFetch = [...queue.value];
        queue.value = [];
        pendingRequest.value = true;

        if (DEBUG_LOGGING) {
            console.log(`[EntityCache] 🚀 Fetching ${entitiesToFetch.length} entities from API:`, entitiesToFetch);
        }

        try {
            const response = await axios.get('/api/entities', {
                params: {
                    entities: entitiesToFetch
                }
            });

            const data = response.data;
            if (DEBUG_LOGGING) {
                console.log(`[EntityCache] 📥 Received response:`, data);
            }

            let totalCached = 0;

            // Cache creatures
            if (data.creatures) {
                if (DEBUG_LOGGING) {
                    console.log(`[EntityCache] 🐉 Caching ${data.creatures.length} creatures`);
                }
                data.creatures.forEach(entity => {
                    cacheEntity('creatures', entity);
                    totalCached++;
                });
            }

            // Cache NPCs
            if (data.npcs) {
                if (DEBUG_LOGGING) {
                    console.log(`[EntityCache] 👤 Caching ${data.npcs.length} NPCs`);
                }
                data.npcs.forEach(entity => {
                    cacheEntity('npcs', entity);
                    totalCached++;
                });
            }

            // Cache characters
            if (data.characters) {
                if (DEBUG_LOGGING) {
                    console.log(`[EntityCache] ⚔️ Caching ${data.characters.length} characters`);
                }
                data.characters.forEach(entity => {
                    cacheEntity('characters', entity);
                    totalCached++;
                });
            }

            // Cache equipment
            if (data.equipment) {
                if (DEBUG_LOGGING) {
                    console.log(`[EntityCache] 🛡️ Caching ${data.equipment.length} equipment`);
                }
                data.equipment.forEach(entity => {
                    cacheEntity('equipment', entity);
                    totalCached++;
                });
            }

            // Cache "not found" entities
            cacheNotFoundEntities(entitiesToFetch, data);

            if (DEBUG_LOGGING) {
                console.log(`[EntityCache] ✅ Successfully cached ${totalCached} entities`);
            }

            // Persist to localStorage
            persist();

        } catch (error) {
            console.error(`[EntityCache] ❌ Failed to fetch entities:`, error);

            // Cache failed entities with short expiration instead of re-queuing
            const now = Date.now();
            const stateMap = { creatures, npcs, characters, equipment };

            entitiesToFetch.forEach(({ type, id }) => {
                const typeKey = getTypeKey(type);

                // Safety check: ensure the typeKey exists in stateMap
                if (!stateMap[typeKey]) {
                    console.error(`[EntityCache] ❌ Invalid entity type "${type}" for id ${id} - skipping cache`);
                    return;
                }

                stateMap[typeKey].value[id] = {
                    id,
                    name: null,
                    illustration_url: null,
                    cached_at: now,
                    expires_at: now + FAILURE_CACHE_DURATION,
                    failed: true,
                };
                if (DEBUG_LOGGING) {
                    console.log(`[EntityCache] ⚠️ Cached failed entity (retry in 1 day): ${type}::${id}`);
                }
            });

            if (DEBUG_LOGGING) {
                console.log(`[EntityCache] ⚠️ Cached ${entitiesToFetch.length} failed entities - will retry after expiration`);
            }

            // Persist failed entities
            persist();
        } finally {
            pendingRequest.value = false;

            // If queue has new items, trigger another fetch
            if (queue.value.length > 0) {
                if (DEBUG_LOGGING) {
                    console.log(`[EntityCache] 🔄 Queue has ${queue.value.length} new items - scheduling another fetch`);
                }
                debouncedFetch();
            }
        }
    }

    // Cache a single entity
    function cacheEntity(typeKey, entity) {
        const stateMap = { creatures, npcs, characters, equipment };

        // Safety check: ensure the typeKey exists in stateMap
        if (!stateMap[typeKey]) {
            console.error(`[EntityCache] ❌ Invalid entity typeKey "${typeKey}" for entity ${entity.id} - skipping cache`);
            return;
        }

        const now = Date.now();
        stateMap[typeKey].value[entity.id] = {
            id: entity.id,
            name: entity.name,
            illustration_url: entity.illustration_url,
            cached_at: now,
            expires_at: now + SUCCESS_CACHE_DURATION,
        };
        if (DEBUG_LOGGING) {
            console.log(`[EntityCache] 💾 Cached ${typeKey.slice(0, -1)}: ${entity.name} (${entity.id}) - expires in 2 weeks`);
        }
    }

    // Cache not found entities
    function cacheNotFoundEntities(requestedEntities, returnedData) {
        const returnedIds = {
            Creature: new Set(returnedData.creatures?.map(e => e.id) ?? []),
            Npc: new Set(returnedData.npcs?.map(e => e.id) ?? []),
            Character: new Set(returnedData.characters?.map(e => e.id) ?? []),
            Equipment: new Set(returnedData.equipment?.map(e => e.id) ?? []),
        };

        let notFoundCount = 0;
        const stateMap = { creatures, npcs, characters, equipment };
        const now = Date.now();

        requestedEntities.forEach(({ type, id }) => {
            if (!returnedIds[type]?.has(id)) {
                const typeKey = getTypeKey(type);

                // Safety check: ensure the typeKey exists in stateMap
                if (!stateMap[typeKey]) {
                    console.error(`[EntityCache] ❌ Invalid entity type "${type}" for id ${id} - skipping not-found cache`);
                    return;
                }

                stateMap[typeKey].value[id] = {
                    id,
                    name: null,
                    illustration_url: null,
                    cached_at: now,
                    expires_at: now + FAILURE_CACHE_DURATION,
                    not_found: true,
                };
                notFoundCount++;
                if (DEBUG_LOGGING) {
                    console.log(`[EntityCache] 🚫 Entity not found (cached as missing for 1 day): ${type}::${id}`);
                }
            }
        });

        if (notFoundCount > 0 && DEBUG_LOGGING) {
            console.log(`[EntityCache] 🚫 Total entities not found: ${notFoundCount}`);
        }
    }

    // Parse entity reference
    function parseEntityRef(entityRef) {
        const match = entityRef.match(/^(\w+)::(.+)$/);
        if (match) {
            return {
                type: match[1],
                id: match[2]
            };
        }
        return { type: null, id: null };
    }

    // Clear cache
    function clearCache() {
        if (DEBUG_LOGGING) {
            console.log(`[EntityCache] 🗑️ Clearing entire cache`);
        }
        creatures.value = {};
        npcs.value = {};
        characters.value = {};
        equipment.value = {};
        queue.value = [];
        localStorage.removeItem(STORAGE_KEY);
        if (DEBUG_LOGGING) {
            console.log(`[EntityCache] ✅ Cache cleared`);
        }
    }

    // Dump cache state
    function dumpCache() {
        const rawState = {
            creatures: creatures.value,
            npcs: npcs.value,
            characters: characters.value,
            equipment: equipment.value,
        };

        console.group('[EntityCache] 📊 Cache State');
        console.log('Creatures:', Object.keys(rawState.creatures).length, rawState.creatures);
        console.log('NPCs:', Object.keys(rawState.npcs).length, rawState.npcs);
        console.log('Characters:', Object.keys(rawState.characters).length, rawState.characters);
        console.log('Equipment:', Object.keys(rawState.equipment).length, rawState.equipment);
        console.log('Queue:', queue.value);
        console.log('Pending Request:', pendingRequest.value);
        console.groupEnd();

        const totalEntities =
            Object.keys(rawState.creatures).length +
            Object.keys(rawState.npcs).length +
            Object.keys(rawState.characters).length +
            Object.keys(rawState.equipment).length;

        console.log(`[EntityCache] 📊 Total cached entities: ${totalEntities}`);

        return {
            creatures: rawState.creatures,
            npcs: rawState.npcs,
            characters: rawState.characters,
            equipment: rawState.equipment,
            queue: queue.value,
            pendingRequest: pendingRequest.value,
            totalEntities
        };
    }

    // Initialize keyboard shortcuts
    function initDebugShortcuts() {
        if (typeof window === 'undefined') return;

        document.addEventListener('keydown', (e) => {
            // Ctrl+Shift+E = Dump cache
            if (e.ctrlKey && e.shiftKey && e.key === 'E') {
                e.preventDefault();
                console.log('[EntityCache] 🔑 Keyboard shortcut: Dump cache');
                dumpCache();
            }

            // Ctrl+Shift+D = Clear cache
            if (e.ctrlKey && e.shiftKey && e.key === 'D') {
                e.preventDefault();
                console.log('[EntityCache] 🔑 Keyboard shortcut: Clear cache');
                clearCache();
            }
        });

        if (DEBUG_LOGGING) {
            console.log('[EntityCache] ⌨️ Debug shortcuts enabled:');
            console.log('  Ctrl+Shift+E = Dump cache state');
            console.log('  Ctrl+Shift+D = Clear cache');
        }
    }

    // Computed state for components
    const state = computed(() => ({
        creatures: creatures.value,
        npcs: npcs.value,
        characters: characters.value,
        equipment: equipment.value,
    }));

    return {
        // State
        state,
        creatures,
        npcs,
        characters,
        equipment,
        queue,
        pendingRequest,

        // Methods
        initialize,
        getEntity,
        isCached,
        requestEntity,
        requestEntities,
        clearCache,
        dumpCache,
        initDebugShortcuts,
    };
});
